import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BellRing, Pencil, Trash2 } from 'lucide-react';
import { api } from '../../services/api';
import { toast } from '../../store/toastStore';
import { formatDate } from '../../utils/format';
import StateBlock from '../ui/StateBlock';
import ConfirmDialog from '../ui/ConfirmDialog';
import { WEEKDAY_KEYS, type ReminderRow } from '../../utils/reminderModel';
import { REMINDERS_QUERY_KEY } from './ReminderFormDialog';

/**
 * W-01 - "התראות שקבעתי": what I created (for myself and for others), with edit and delete.
 * Only the creator sees and changes it here; the server refuses anyone else.
 */
export default function MyReminders({ selfId, onEdit }: { selfId: string; onEdit: (r: ReminderRow) => void }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [confirm, setConfirm] = useState<ReminderRow | null>(null);
  const { data, isPending, isError, error, refetch, isRefetching } = useQuery({
    queryKey: REMINDERS_QUERY_KEY,
    queryFn: () => api.getMyReminders(),
  });
  const rows: ReminderRow[] = data?.data ?? [];

  const remove = useMutation({
    mutationFn: (id: string) => api.deleteReminder(id),
    onSuccess: () => {
      toast('success', t('reminder.deleted'));
      setConfirm(null);
      void queryClient.invalidateQueries({ queryKey: REMINDERS_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
    onError: () => { setConfirm(null); toast('error', t('reminder.deleteFailed')); },
  });

  const when = (r: ReminderRow) => {
    const day = formatDate(new Date(`${r.start_date}T12:00:00`));
    if (r.repeat_kind === 'daily') return t('reminder.summaryDaily', { time: r.start_time });
    if (r.repeat_kind === 'weekly') {
      const days = (r.weekdays ?? []).map((d) => t(`reminder.weekday.${WEEKDAY_KEYS[d]}`)).join(', ');
      return t('reminder.summaryWeekly', { days, time: r.start_time });
    }
    return t('reminder.summaryOnce', { date: day, time: r.start_time });
  };

  if (isPending) return <div className="px-5"><StateBlock kind="loading" size="panel" rows={3} /></div>;
  if (isError) {
    return (
      <StateBlock kind="error" size="panel" title={t('reminder.listError')} error={error} onRetry={() => { void refetch(); }} retrying={isRefetching} />
    );
  }
  if (rows.length === 0) {
    return <StateBlock kind="empty" size="panel" icon={BellRing} title={t('reminder.emptyTitle')} description={t('reminder.emptyLine')} />;
  }

  return (
    <>
      <ul data-my-reminders="">
        {rows.map((r) => (
          <li key={r.id} className="flex items-start gap-2 border-b border-border px-5 py-3 last:border-0">
            <span className="block min-w-0 flex-1">
              <span className="block text-sm font-medium text-text"><bdi>{r.message}</bdi></span>
              <span className="mt-0.5 block text-xs text-text-2">
                {when(r)}
                {r.repeat_kind !== 'once' && r.until_date ? ` · ${t('reminder.summaryUntil', { date: formatDate(new Date(`${r.until_date}T12:00:00`)) })}` : ''}
                {r.repop_minutes ? ` · ${t('reminder.repopEvery', { count: r.repop_minutes })}` : ''}
                {r.keep_until_done ? ` · ${t('reminder.summaryKeep')}` : ''}
              </span>
              <span className="mt-0.5 block text-xs text-text-2">
                {r.user_id === selfId ? t('reminder.forMe') : t('reminder.forName', { name: r.recipient_name ?? '' })}
              </span>
            </span>
            <button type="button" className="btn-icon text-text-2" aria-label={t('reminder.edit')} onClick={() => onEdit(r)}>
              <Pencil size={16} strokeWidth={1.75} aria-hidden="true" />
            </button>
            <button type="button" className="btn-icon text-text-2" aria-label={t('reminder.delete')} onClick={() => setConfirm(r)}>
              <Trash2 size={16} strokeWidth={1.75} aria-hidden="true" />
            </button>
          </li>
        ))}
      </ul>
      {confirm && (
        <ConfirmDialog
          title={t('reminder.deleteTitle')}
          description={t('reminder.deleteBody', { message: confirm.message })}
          confirmLabel={t('reminder.delete')}
          danger
          loading={remove.isPending}
          onConfirm={() => remove.mutate(confirm.id)}
          onCancel={() => setConfirm(null)}
        />
      )}
    </>
  );
}
